import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarDots, Plus, PencilSimple, Trash, CaretLeft, CaretRight, InstagramLogo, FacebookLogo, TiktokLogo, Globe, EnvelopeSimple, X, GearSix } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PLATFORMS = [
  { value: 'instagram', label: 'Instagram', icon: InstagramLogo },
  { value: 'facebook', label: 'Facebook', icon: FacebookLogo },
  { value: 'tiktok', label: 'TikTok', icon: TiktokLogo },
  { value: 'blog', label: 'Blog/Site', icon: Globe },
  { value: 'email', label: 'Email', icon: EnvelopeSimple },
];
const FORMATS = [
  { value: 'post', label: 'Post' },
  { value: 'reels', label: 'Reels/Vídeo' },
  { value: 'story', label: 'Story' },
  { value: 'carrossel', label: 'Carrossel' },
];
const STATUSES = [
  { value: 'ideia', label: 'Ideia', color: 'bg-gray-100 text-gray-500' },
  { value: 'planeado', label: 'Planeado', color: 'bg-amber-50 text-amber-600' },
  { value: 'gravado', label: 'Gravado/Escrito', color: 'bg-purple-50 text-purple-600' },
  { value: 'agendado', label: 'Agendado', color: 'bg-blue-50 text-blue-600' },
  { value: 'publicado', label: 'Publicado', color: 'bg-brand-mint/20 text-brand-green' },
];
const EMPTY_FORM = { date: new Date().toISOString().slice(0, 10), title: '', platform: 'instagram', format: 'post', pillar: '', subtheme: '', status: 'ideia', notes: '' };

const platformMeta = (v) => PLATFORMS.find(p => p.value === v) || PLATFORMS[0];
const statusMeta = (v) => STATUSES.find(s => s.value === v) || STATUSES[0];

export default function CalendarioEditorialPage() {
  const { user } = useAuth();
  const confirmDelete = useConfirmDelete();
  const canEdit = ['admin', 'nutricionista', 'marketing_editor'].includes(user?.role);
  const [monthCursor, setMonthCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [items, setItems] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platformFilter, setPlatformFilter] = useState('');
  const [pillarFilter, setPillarFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showPillars, setShowPillars] = useState(false);
  const [editPillars, setEditPillars] = useState([]);
  const [newPillarLabel, setNewPillarLabel] = useState('');

  const monthLabel = new Date(monthCursor.y, monthCursor.m, 1).toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });
  const rangeFrom = `${monthCursor.y}-${String(monthCursor.m + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(monthCursor.y, monthCursor.m + 1, 0).getDate();
  const rangeTo = `${monthCursor.y}-${String(monthCursor.m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: rangeFrom, date_to: rangeTo };
      if (platformFilter) params.platform = platformFilter;
      if (pillarFilter) params.pillar = pillarFilter;
      if (statusFilter) params.status = statusFilter;
      const { data } = await axios.get(`${API}/api/marketing/calendar`, { params });
      setItems(data);
    } catch { toast.error('Erro ao carregar calendário'); } finally { setLoading(false); }
  }, [rangeFrom, rangeTo, platformFilter, pillarFilter, statusFilter]);
  const fetchPillars = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/marketing/content-pillars`); setPillars(data.pillars || []); } catch {}
  }, []);
  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchPillars(); }, [fetchPillars]);

  const pillarLabel = (val) => pillars.find(p => p.value === val)?.label || val;

  const openCreate = () => { setEditId(null); setForm({ ...EMPTY_FORM, date: rangeFrom > new Date().toISOString().slice(0, 10) ? rangeFrom : new Date().toISOString().slice(0, 10) }); setShowForm(true); };
  const openEdit = (it) => { setEditId(it.id); setForm({ date: it.date, title: it.title, platform: it.platform, format: it.format, pillar: it.pillar || '', subtheme: it.subtheme || '', status: it.status, notes: it.notes || '' }); setShowForm(true); };

  const save = async () => {
    if (!form.date || !form.title.trim()) { toast.error('Data e título são obrigatórios'); return; }
    try {
      if (editId) await axios.put(`${API}/api/marketing/calendar/${editId}`, form);
      else await axios.post(`${API}/api/marketing/calendar`, form);
      toast.success(editId ? 'Conteúdo atualizado' : 'Conteúdo criado');
      setShowForm(false); fetchItems();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao guardar'); }
  };
  const del = async (id) => {
    const it = items.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o conteúdo "${it?.title || ''}" do calendário editorial.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/marketing/calendar/${id}`); toast.success('Eliminado'); fetchItems(); } catch { toast.error('Erro'); }
  };
  const quickStatus = async (it, status) => { try { await axios.put(`${API}/api/marketing/calendar/${it.id}`, { status }); fetchItems(); } catch { toast.error('Erro'); } };

  const savePillars = async () => {
    try { const { data } = await axios.put(`${API}/api/marketing/content-pillars`, { pillars: editPillars }); setPillars(data.pillars); toast.success('Pilares guardados'); setShowPillars(false); } catch { toast.error('Erro'); }
  };
  const addPillar = () => {
    const label = newPillarLabel.trim();
    if (!label) return;
    setEditPillars([...editPillars, { value: label.toLowerCase().replace(/\s+/g, '_'), label }]);
    setNewPillarLabel('');
  };

  const grouped = items.reduce((acc, it) => { (acc[it.date] = acc[it.date] || []).push(it); return acc; }, {});
  const dates = Object.keys(grouped).sort();

  return (
    <div className="fade-in" data-testid="calendario-editorial-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-medium text-2xl text-brand-text flex items-center gap-2"><CalendarDots size={24} weight="duotone" className="text-brand-green" /> Calendário Editorial</h2>
          <p className="text-sm text-brand-muted mt-1">{items.length} conteúdo(s) em {monthLabel}</p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <button onClick={() => { setEditPillars([...pillars]); setShowPillars(true); }} className="bg-white border border-brand-border text-brand-text rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="manage-pillars-btn"><GearSix size={16} /> Pilares</button>
            <button onClick={openCreate} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="new-content-btn"><Plus size={16} /> Novo conteúdo</button>
          </div>
        )}
      </div>

      {/* Month nav */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => setMonthCursor(c => c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 })} className="p-2 rounded-full bg-white border border-brand-border text-brand-muted hover:text-brand-green" data-testid="prev-month-btn"><CaretLeft size={16} /></button>
        <span className="font-medium text-brand-text capitalize min-w-[140px] text-center" data-testid="month-label">{monthLabel}</span>
        <button onClick={() => setMonthCursor(c => c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 })} className="p-2 rounded-full bg-white border border-brand-border text-brand-muted hover:text-brand-green" data-testid="next-month-btn"><CaretRight size={16} /></button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-5">
        <select value={platformFilter} onChange={e => setPlatformFilter(e.target.value)} className="border border-brand-border rounded-full px-3 py-1.5 text-xs bg-white text-brand-text" data-testid="filter-platform">
          <option value="">Todas as plataformas</option>
          {PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <select value={pillarFilter} onChange={e => setPillarFilter(e.target.value)} className="border border-brand-border rounded-full px-3 py-1.5 text-xs bg-white text-brand-text" data-testid="filter-pillar">
          <option value="">Todos os pilares</option>
          {pillars.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-brand-border rounded-full px-3 py-1.5 text-xs bg-white text-brand-text" data-testid="filter-status">
          <option value="">Todos os estados</option>
          {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {loading ? <div className="text-center py-12 text-brand-muted">A carregar...</div> : dates.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-brand-border rounded-2xl">
          <CalendarDots size={44} className="mx-auto text-brand-placeholder mb-3" weight="duotone" />
          <p className="text-brand-muted">Sem conteúdos planeados para este mês.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {dates.map(date => (
            <div key={date}>
              <p className="text-xs font-medium text-brand-muted mb-2">{new Date(date + 'T00:00:00').toLocaleDateString('pt-PT', { weekday: 'long', day: '2-digit', month: 'long' })}</p>
              <div className="space-y-2">
                {grouped[date].map(it => {
                  const PIcon = platformMeta(it.platform).icon;
                  const sm = statusMeta(it.status);
                  return (
                    <div key={it.id} className="bg-white border border-brand-border rounded-2xl p-4 flex items-center gap-4" data-testid={`content-card-${it.id}`}>
                      <div className="w-9 h-9 rounded-full bg-brand-mint/20 flex items-center justify-center flex-shrink-0"><PIcon size={18} className="text-brand-green" weight="duotone" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-brand-text truncate">{it.title}</p>
                        <p className="text-[10px] text-brand-muted flex items-center gap-1.5 flex-wrap mt-0.5">
                          <span>{platformMeta(it.platform).label}</span> · <span>{FORMATS.find(f => f.value === it.format)?.label}</span>
                          {it.pillar && <span className="px-1.5 py-0.5 rounded-full bg-brand-bg">{pillarLabel(it.pillar)}</span>}
                          {it.subtheme && <span className="px-1.5 py-0.5 rounded-full bg-brand-bg">{it.subtheme}</span>}
                        </p>
                      </div>
                      {canEdit ? (
                        <select value={it.status} onChange={e => quickStatus(it, e.target.value)} className={`text-[10px] font-medium px-2 py-1 rounded-full border-0 ${sm.color}`} data-testid={`status-select-${it.id}`}>
                          {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      ) : (
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${sm.color}`} data-testid={`status-badge-${it.id}`}>{sm.label}</span>
                      )}
                      {canEdit && (
                        <div className="flex gap-1 flex-shrink-0">
                          <button onClick={() => openEdit(it)} className="p-1.5 text-brand-placeholder hover:text-brand-green" data-testid={`edit-content-${it.id}`}><PencilSimple size={14} /></button>
                          <button onClick={() => del(it.id)} className="p-1.5 text-brand-placeholder hover:text-red-500" data-testid={`delete-content-${it.id}`}><Trash size={14} /></button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editId ? 'Editar conteúdo' : 'Novo conteúdo'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-brand-muted mb-1 block">Data</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-xl border-brand-border" data-testid="content-date-input" /></div>
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Estado</Label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="content-status-input">
                  {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Título</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ex: Reels Risotão de curgete" className="rounded-xl border-brand-border" data-testid="content-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Plataforma</Label>
                <select value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="content-platform-input">
                  {PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Formato</Label>
                <select value={form.format} onChange={e => setForm({ ...form, format: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="content-format-input">
                  {FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Pilar</Label>
                <select value={form.pillar} onChange={e => setForm({ ...form, pillar: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="content-pillar-input">
                  <option value="">Sem pilar</option>
                  {pillars.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Subtema</Label><Input value={form.subtheme} onChange={e => setForm({ ...form, subtheme: e.target.value })} placeholder="Ex: jantares rápidos" className="rounded-xl border-brand-border" data-testid="content-subtheme-input" /></div>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Notas / legenda</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={4} className="rounded-xl border-brand-border" data-testid="content-notes-input" /></div>
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-content-btn">{editId ? 'Guardar' : 'Criar conteúdo'}</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pillars management dialog */}
      <Dialog open={showPillars} onOpenChange={setShowPillars}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Gerir pilares de conteúdo</DialogTitle></DialogHeader>
          <div className="space-y-2 mb-3">
            {editPillars.map((p, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input value={p.label} onChange={e => { const u = [...editPillars]; u[i] = { ...u[i], label: e.target.value }; setEditPillars(u); }} className="flex-1 rounded-lg border-brand-border text-xs h-8" data-testid={`pillar-input-${i}`} />
                <button onClick={() => setEditPillars(editPillars.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600" data-testid={`pillar-remove-${i}`}><X size={14} /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mb-3">
            <Input value={newPillarLabel} onChange={e => setNewPillarLabel(e.target.value)} placeholder="Novo pilar (ex: Risotão)" className="flex-1 rounded-lg border-brand-border text-xs h-8" data-testid="new-pillar-input" />
            <button onClick={addPillar} className="text-xs bg-brand-bg border border-brand-border rounded-lg px-3 h-8" data-testid="add-pillar-btn">Adicionar</button>
          </div>
          <button onClick={savePillars} className="w-full bg-brand-green text-white rounded-full py-2.5 text-sm font-medium" data-testid="save-pillars-btn">Guardar pilares</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
